/**
 * CHU palette tokens for the admin panel.
 *
 * Sourced from the shipped brand system rather than invented fresh:
 * `docker/librechat-chu/chu-theme.css` (Fern Green primary, Gumleaf Teal
 * focus ring, Outback Night ink, "brand guidelines v1.1" neutral-charcoal
 * dark mode) and `docker/librechat-chu/chu-login.css` (the cream ground,
 * white card, wattle-green accent of the CHU-GPT sign-in moment). The admin
 * panel is a distinct app, not LibreChat, so it gets its own token set
 * rather than reusing LibreChat's CSS files directly — but every colour
 * below traces back to one of those two files, never a new hue.
 */

export type ThemeMode = 'light' | 'dark'

export interface ThemeTokens {
  /** Page background. */
  background: string
  /** Card / panel surface. */
  surface: string
  /** A step up from `surface` — hover states, nested panels. */
  surfaceRaised: string
  /** Hairline borders and dividers. */
  border: string
  textPrimary: string
  textSecondary: string
  /** Fern Green — primary actions, active nav state, links. */
  brand: string
  brandHover: string
  /** Text/icon colour placed on top of a `brand`-coloured surface. */
  brandContrast: string
  /** Gumleaf Teal — focus rings only, per brand guidelines. */
  ring: string
  danger: string
  dangerContrast: string
  /** The nav rail keeps the same Outback Night ink in both themes. */
  navBackground: string
  navText: string
  navTextActive: string
  shadow: string
}

const light: ThemeTokens = {
  background: '#F7F1E8',
  surface: '#FFFFFF',
  surfaceRaised: '#FBF8F2',
  border: '#E4DED2',
  textPrimary: '#06272E',
  textSecondary: '#3D565E',
  brand: '#00A86E',
  brandHover: '#00784F',
  brandContrast: '#FFFFFF',
  ring: '#00948F',
  danger: '#B3261E',
  dangerContrast: '#FFFFFF',
  navBackground: '#06272E',
  navText: 'rgba(255, 255, 255, 0.72)',
  navTextActive: '#FFFFFF',
  shadow: '0 18px 50px rgba(6, 39, 46, 0.12)',
}

const dark: ThemeTokens = {
  background: '#171717',
  surface: '#1F1F1F',
  surfaceRaised: '#272727',
  border: '#3A3A3A',
  textPrimary: '#F2F1EC',
  textSecondary: '#B7C4C2',
  brand: '#33BF8B',
  brandHover: '#66D1A8',
  brandContrast: '#06272E',
  ring: '#33BFBB',
  danger: '#F2938C',
  dangerContrast: '#3D0A06',
  navBackground: '#06272E',
  navText: 'rgba(255, 255, 255, 0.72)',
  navTextActive: '#FFFFFF',
  shadow: '0 18px 50px rgba(0, 0, 0, 0.5)',
}

export const themes: Record<ThemeMode, ThemeTokens> = { light, dark }

const STORAGE_KEY = 'chu-gpt-admin-theme'

/** Reads the operator's stored preference, falling back to the OS setting. */
export function getPreferredTheme(): ThemeMode {
  if (typeof window === 'undefined') {
    return 'light'
  }
  const stored = window.localStorage.getItem(STORAGE_KEY)
  if (stored === 'light' || stored === 'dark') {
    return stored
  }
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export function storeThemePreference(mode: ThemeMode): void {
  window.localStorage.setItem(STORAGE_KEY, mode)
}
